import { BrowserRouter, Routes, Route, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { MainLayout } from './components/layout';
import { LoadingScreen } from './components/ui/LoadingScreen';
import { ContractLens } from './pages/products/ContractLens';

import { CustomerSelect } from './pages/auth/CustomerSelect';
import { LoginCallback } from './pages/auth/LoginCallback';
import { LoginPage } from './pages/auth/LoginPage';
import { CustomersAdmin, UsersAdmin, SettingsAdmin, ContractLensAdmin, GlobalAdminsAdmin } from './pages/admin';
import { RequireRole } from './components/auth/RequireRole';
import { useAuth } from './hooks/useAuth';
import './index.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 1,
    },
  },
});

/**
 * The /login route serves two purposes:
 *  - completing a redirect from the central Lynki.Auth site (a `token` is present)
 *  - the portal's own two-step login form with mandatory email 2FA
 * See docs/SECURITY-2FA.md.
 */
function LoginRoute() {
  const [searchParams] = useSearchParams();
  return searchParams.get('token') ? <LoginCallback /> : <LoginPage />;
}

function AuthenticatedApp() {
  const { isLoading, isAuthenticated } = useAuth();
  const location = useLocation();

  // Allow /login route without authentication (token callback or 2FA login form)
  if (location.pathname === '/login') {
    return <LoginRoute />;
  }

  if (isLoading) {
    return <LoadingScreen message="Authenticating..." />;
  }

  if (!isAuthenticated) {
    return <LoadingScreen message="Redirecting to login..." />;
  }

  return (
    <Routes>
      {/* Customer Selection (no layout) */}
      <Route path="/select-customer" element={<CustomerSelect />} />

      {/* Main Layout Routes */}
      <Route element={<MainLayout />}>
        <Route path="/" element={<Navigate to="/products/contractlens" replace />} />
        <Route path="/products/contractlens" element={<ContractLens />} />


        {/* Admin Routes */}
        <Route path="/admin/customers" element={
          <RequireRole roles={['GlobalAdmin', 'CustomerAdmin']}>
            <CustomersAdmin />
          </RequireRole>
        } />
        <Route path="/admin/users" element={
          <RequireRole roles={['GlobalAdmin', 'UserAdmin', 'CustomerAdmin', 'ProgramAdmin']}>
            <UsersAdmin />
          </RequireRole>
        } />
        <Route path="/admin/settings" element={
          <RequireRole roles={['GlobalAdmin']}>
            <SettingsAdmin />
          </RequireRole>
        } />
        <Route path="/admin/global-admins" element={
          <RequireRole roles={['GlobalAdmin']}>
            <GlobalAdminsAdmin />
          </RequireRole>
        } />
        <Route path="/admin/contractlens" element={
          <RequireRole roles={['GlobalAdmin']}>
            <ContractLensAdmin />
          </RequireRole>
        } />
      </Route>

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthenticatedApp />
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
